import { Building2, Users, MapPin, Wifi, DoorOpen, CreditCard, Calendar, Smartphone, Shield, Globe, Sparkles, Bell, MonitorSmartphone, Target, Activity, Rocket, Lock, Eye, Database, Network, CheckCircle } from 'lucide-react';
import type { ProductFeatureConfig } from './types';

// Coworking Space Management Suite - Key Benefits & Capabilities
// Sources incorporated: Regus, WeWork, Spaces, Work.Life, Official Space (feature language adapted)
export const coworkingManagementSuiteFeatures: ProductFeatureConfig = {
  keyBenefits: [
    {
      icon: Building2,
      title: 'Flexible Workspace Products (Hot Desk, Dedicated, Private)',
      description: 'Offer hot desks, dedicated desks, private offices and day passes with flexible terms and transparent pricing.',
      metrics: 'Hot desk • Dedicated desk • Private office • Day pass',
      color: 'from-purple-600 to-indigo-700'
    },
    {
      icon: Calendar,
      title: 'Real‑Time Booking for Desks & Meeting Rooms',
      description: 'Instant online booking with availability view, hourly/day rates, credits, and add‑ons (AV, catering).',
      metrics: 'Instant booking • Availability calendar • Credits',
      color: 'from-blue-600 to-cyan-600'
    },
    {
      icon: Wifi,
      title: 'Enterprise Wi‑Fi & IT Ready Out‑of‑the‑Box',
      description: 'Secure Wi‑Fi, guest networks, VLAN segmentation, and device onboarding built for hybrid teams.',
      metrics: '802.1X • Guest Wi‑Fi • VLANs',
      color: 'from-emerald-600 to-teal-600'
    },
    {
      icon: DoorOpen,
      title: 'Smart Access Control & 24/7 Entry',
      description: 'Mobile keys, QR/ NFC access, visitor passes, lockers and after‑hours policies across locations.',
      metrics: 'Mobile keys • QR/NFC • Visitor passes',
      color: 'from-slate-600 to-gray-700'
    },
    {
      icon: Users,
      title: 'Community Directory & Events',
      description: 'Member profiles, interest tags, announcements, event RSVPs and networking to grow vibrant communities.',
      metrics: 'Profiles • Events • Announcements',
      color: 'from-pink-600 to-rose-600'
    },
    {
      icon: CreditCard,
      title: 'Automated Billing & Memberships',
      description: 'Plans, credits, deposits, pro‑rata, add‑ons, usage billing, invoicing, dunning and multi‑gateway payments.',
      metrics: 'Plans • Usage billing • Dunning',
      color: 'from-amber-600 to-orange-600'
    },
    {
      icon: MapPin,
      title: 'Multi‑Site, Global Locations',
      description: 'Operate multiple sites with unified inventory, role‑based control, and global product catalogs.',
      metrics: 'Multi‑site • Global inventory',
      color: 'from-indigo-600 to-purple-700'
    },
    {
      icon: Smartphone,
      title: 'Member Mobile App (iOS & Android)',
      description: 'Book spaces, open doors, manage invoices, RSVP to events and chat with the community on the go.',
      metrics: 'Booking • Access • Wallet • Chat',
      color: 'from-cyan-600 to-sky-600'
    },
    {
      icon: Bell,
      title: 'Front‑of‑House & Visitor Management',
      description: 'Guest pre‑registration, NDA capture, badge printing, host notifications and real‑time occupancy.',
      metrics: 'Pre‑reg • Badges • Host alerts',
      color: 'from-lime-600 to-green-600'
    },
    {
      icon: MonitorSmartphone,
      title: 'Digital Signage & Room Displays',
      description: 'Door tablets and lobby screens show live availability, meeting schedules and brand content.',
      metrics: 'Live schedules • Availability',
      color: 'from-fuchsia-600 to-pink-600'
    },
  ],

  advancedCapabilities: [
    {
      icon: Sparkles,
      title: 'AI Demand Forecasting & Dynamic Pricing',
      description: 'Predict demand by day/location and apply smart pricing rules to maximize occupancy & yield.',
      metrics: '+8–15% revenue uplift (typical)',
      color: 'from-blue-600 to-indigo-700'
    },
    {
      icon: Shield,
      title: 'AI Fraud & Abuse Prevention',
      description: 'Detect suspicious bookings, shared passes, and access anomalies with automated enforcement.',
      metrics: 'Real‑time risk scoring',
      color: 'from-red-600 to-rose-600'
    },
    {
      icon: Globe,
      title: 'Global Credits & Reciprocity',
      description: 'Let members book desks and rooms across your global network with credits and partner reciprocity.',
      metrics: 'Cross‑site credits • Reciprocity',
      color: 'from-emerald-600 to-teal-600'
    },
    {
      icon: Calendar,
      title: 'Advanced Meeting Services',
      description: 'AV presets, catering workflows, visitor bundles, and automatic chargeback to bookings.',
      metrics: 'AV • Catering • Chargeback',
      color: 'from-amber-600 to-orange-600'
    },
    {
      icon: Wifi,
      title: 'SLA‑Backed Network & Device Management',
      description: 'Managed Wi‑Fi, IoT printers, phones and sensors with device identity and policy control.',
      metrics: 'MFA • Device identity',
      color: 'from-slate-600 to-gray-700'
    },
    {
      icon: Users,
      title: 'Corporate Team & Allowance Management',
      description: 'Company hubs, budgets, cost centers, SSO provisioning and consolidated invoicing.',
      metrics: 'SSO • Cost centers • Budgets',
      color: 'from-violet-600 to-purple-700'
    },
    {
      icon: DoorOpen,
      title: 'Access + Booking Orchestration',
      description: 'Automatic door rights on check‑in, late‑exit surcharges, and locker allocation per booking.',
      metrics: 'Auto‑provisioned access',
      color: 'from-green-600 to-emerald-600'
    },
    {
      icon: Smartphone,
      title: 'Member Engagement Scores',
      description: 'AI analyzes attendance, spend, events and posts to predict churn and trigger outreach.',
      metrics: 'Churn risk insights',
      color: 'from-cyan-600 to-sky-600'
    }
  ],

  securityPerformance: [
    {
      icon: Target,
      title: 'Compliance Excellence Framework',
      description: 'Automated compliance monitoring for SOC 2, ISO 27001, GDPR, and industry-specific regulations',
      metrics: 'Complete regulatory compliance',
      color: 'from-indigo-500 to-purple-600'
    },
    {
      icon: Activity,
      title: 'Security Performance Analytics',
      description: 'Advanced security metrics with threat landscape analysis and security posture optimization',
      metrics: 'Optimized security posture',
      color: 'from-yellow-500 to-amber-600'
    },
    {
      icon: Rocket,
      title: 'Scalable Security Infrastructure',
      description: 'Enterprise-grade security platform supporting unlimited users with global threat intelligence',
      metrics: 'Unlimited security scale',
      color: 'from-emerald-500 to-green-600'
    },
    {
      icon: Shield,
      title: 'Advanced Firewall & DDoS Protection',
      description: 'Next-generation firewall with AI-powered DDoS mitigation and intrusion prevention systems',
      metrics: '99.99% attack mitigation',
      color: 'from-red-500 to-pink-600'
    },
    {
      icon: Lock,
      title: 'Multi-Factor Authentication Suite',
      description: 'Enterprise SSO with biometric authentication, hardware tokens, and risk-based access controls',
      metrics: 'Zero-trust access control',
      color: 'from-blue-500 to-indigo-600'
    },
    {
      icon: Eye,
      title: 'Security Information & Event Management',
      description: 'Real-time SIEM with behavioral analytics, threat hunting, and automated incident correlation',
      metrics: 'Real-time threat visibility',
      color: 'from-purple-500 to-pink-600'
    },
    {
      icon: Database,
      title: 'Data Loss Prevention & Backup',
      description: 'Automated data classification, leakage prevention, and secure backup with point-in-time recovery',
      metrics: 'Zero data loss guarantee',
      color: 'from-green-500 to-emerald-600'
    },
    {
      icon: Network,
      title: 'Network Segmentation & Monitoring',
      description: 'Micro-segmentation with network traffic analysis and anomaly detection for lateral movement prevention',
      metrics: 'Complete network visibility',
      color: 'from-orange-500 to-red-600'
    },
    {
      icon: CheckCircle,
      title: 'Continuous Security Assessment',
      description: 'Automated vulnerability scanning, security testing, and continuous compliance validation',
      metrics: 'Proactive security validation',
      color: 'from-cyan-500 to-blue-600'
    }
  ]
};
